import { ColumnMetadata, LayerStats } from '@/components/maplibre/types';
import { getDefaultStyles } from '@/utils/layer-sources/style-defaults';
import { DEFAULT_STYLE_VALUES, LABEL_MODE } from './style-constants';
import {
  ColorMode,
  CustomColorsConfig,
  IconCategory,
  IconCustomMapping,
  IconMode,
  LabelStyle,
  LineStyle,
  NumericMode,
  OpacityMode,
  PointStyle,
  PolygonStyle,
  StyleV2,
} from './style-v2';

const STYLE_MODE = {
  FIXED: 'fixed',
  RANGE: 'range',
  CATEGORY: 'category',
} as const;

export interface StyleModification {
  property: string;
  reason:
    | 'column_not_found'
    | 'column_type_mismatch'
    | 'category_values_filtered'
    | 'data_range_adjusted'
    | 'section_missing';
  details?: string;
}

export interface StyleValidationResult {
  validatedStyle: StyleV2;
  wasModified: boolean;
  modifications: StyleModification[];
}

function columnIdEquals(a: string | number | null, b: string | number | null): boolean {
  if (a === null || b === null) return a === b;
  return a == b;
}

/**
 * Finds a stats field by columnId, supporting both direct ID match
 * and UUID→name resolution via columnMetadata.
 *
 * Stats fields use column names as `field.id` (e.g. "population"),
 * while styles may store UUIDs as `columnId`. When a direct match fails,
 * we resolve the UUID to a column name via columnMetadata and match by `field.name`.
 */
function findStatsField(
  columnId: string | number | null,
  stats: LayerStats,
  columnMetadata?: ColumnMetadata[]
): (typeof stats.fields)[number] | undefined {
  if (columnId === null || columnId === undefined) return undefined;

  // Direct match by field.id
  const direct = stats.fields.find(f => columnIdEquals(f.id, columnId));
  if (direct) return direct;

  // Resolve UUID → column name via metadata, then match by field.name
  if (columnMetadata) {
    const col = columnMetadata.find(c => columnIdEquals(c.column_id, columnId));
    if (col) {
      return stats.fields.find(f => f.name === col.column_name);
    }
  }

  return undefined;
}

function columnExists(
  columnId: string | number | null,
  stats: LayerStats,
  columnMetadata?: ColumnMetadata[]
): boolean {
  if (columnId === null || columnId === undefined) return true;
  return findStatsField(columnId, stats, columnMetadata) !== undefined;
}

function isValidForRange(
  columnId: string | number | null,
  stats: LayerStats,
  columnMetadata?: ColumnMetadata[]
): boolean {
  if (columnId === null || columnId === undefined) return false;
  const field = findStatsField(columnId, stats, columnMetadata);
  if (!field || field.type !== 'number') return false;
  return field.count_distinct >= 2;
}

function getValidCategoryValues(
  columnId: string | number | null,
  stats: LayerStats,
  columnMetadata?: ColumnMetadata[]
): Set<string> {
  if (columnId === null || columnId === undefined) return new Set();
  const field = findStatsField(columnId, stats, columnMetadata);
  if (!field) return new Set();
  return new Set(Object.keys(field.values));
}

function getColumnMinMax(
  columnId: string | number | null,
  stats: LayerStats,
  columnMetadata?: ColumnMetadata[]
): { min: number; max: number } | null {
  if (columnId === null || columnId === undefined) return null;
  const field = findStatsField(columnId, stats, columnMetadata);
  if (!field || field.type !== 'number') return null;
  return { min: field.min, max: field.max };
}

function createDefaultColorMode(): ColorMode {
  return {
    mode: STYLE_MODE.FIXED,
    value: DEFAULT_STYLE_VALUES.polygonFillColor,
  };
}

function createDefaultNumericMode(defaultValue: number): NumericMode {
  return {
    mode: STYLE_MODE.FIXED,
    value: defaultValue,
  };
}

function createDefaultOpacityMode(defaultValue: number): OpacityMode {
  return {
    mode: STYLE_MODE.FIXED,
    value: defaultValue,
  };
}

function createDefaultIconMode(): IconMode {
  return {
    mode: STYLE_MODE.FIXED,
    value: null,
  };
}

function validateColorMode(
  colorMode: ColorMode,
  stats: LayerStats,
  propertyPath: string,
  modifications: StyleModification[],
  options: { clampDataRange?: boolean } = {},
  columnMetadata?: ColumnMetadata[]
): { color: ColorMode; modified: boolean } {
  const { clampDataRange = false } = options;

  if (colorMode.mode === STYLE_MODE.FIXED) {
    return { color: colorMode, modified: false };
  }

  const columnId = colorMode.columnId;

  if (!columnExists(columnId, stats, columnMetadata)) {
    modifications.push({
      property: propertyPath,
      reason: 'column_not_found',
      details: `Column ${columnId} not found in stats`,
    });
    return { color: createDefaultColorMode(), modified: true };
  }

  if (colorMode.mode === STYLE_MODE.RANGE && !isValidForRange(columnId, stats, columnMetadata)) {
    modifications.push({
      property: propertyPath,
      reason: 'column_type_mismatch',
      details: `Column ${columnId} is not numeric, cannot use range mode`,
    });
    return { color: createDefaultColorMode(), modified: true };
  }

  if (colorMode.mode === STYLE_MODE.RANGE && clampDataRange) {
    const statsMinMax = getColumnMinMax(columnId, stats, columnMetadata);
    if (statsMinMax) {
      const { min: sMin, max: sMax } = statsMinMax;
      const origMin = colorMode.dataMin;
      const origMax = colorMode.dataMax;

      let newMin = origMin !== undefined ? Math.max(sMin, Math.min(sMax, origMin)) : origMin;
      let newMax = origMax !== undefined ? Math.max(sMin, Math.min(sMax, origMax)) : origMax;

      if (newMin !== undefined && newMax !== undefined && newMin >= newMax) {
        newMin = sMin;
        newMax = sMax;
      }

      if (newMin !== origMin || newMax !== origMax) {
        modifications.push({
          property: propertyPath,
          reason: 'data_range_adjusted',
          details: `Adjusted dataMin/dataMax from ${origMin}/${origMax} to ${newMin}/${newMax}`,
        });
        return {
          color: { ...colorMode, dataMin: newMin, dataMax: newMax },
          modified: true,
        };
      }
    }
  }

  if (colorMode.mode === STYLE_MODE.CATEGORY && colorMode.customColors) {
    const validValues = getValidCategoryValues(columnId, stats, columnMetadata);
    const filteredColors = colorMode.customColors.filter((cc: CustomColorsConfig) =>
      validValues.has(cc.category)
    );

    if (filteredColors.length !== colorMode.customColors.length) {
      modifications.push({
        property: propertyPath,
        reason: 'category_values_filtered',
        details: `Filtered ${colorMode.customColors.length - filteredColors.length} custom colors`,
      });
      return {
        color: {
          ...colorMode,
          customColors: filteredColors,
        },
        modified: true,
      };
    }
  }

  return { color: colorMode, modified: false };
}

function validateNumericMode(
  numericMode: NumericMode,
  stats: LayerStats,
  propertyPath: string,
  defaultValue: number,
  modifications: StyleModification[],
  columnMetadata?: ColumnMetadata[]
): { numeric: NumericMode; modified: boolean } {
  if (numericMode.mode === STYLE_MODE.FIXED) {
    return { numeric: numericMode, modified: false };
  }

  const columnId = numericMode.columnId;

  if (!columnExists(columnId, stats, columnMetadata)) {
    modifications.push({
      property: propertyPath,
      reason: 'column_not_found',
      details: `Column ${columnId} not found in stats`,
    });
    return { numeric: createDefaultNumericMode(defaultValue), modified: true };
  }

  if (!isValidForRange(columnId, stats, columnMetadata)) {
    modifications.push({
      property: propertyPath,
      reason: 'column_type_mismatch',
      details: `Column ${columnId} is not valid for range (needs numeric type with 2+ distinct values)`,
    });
    return { numeric: createDefaultNumericMode(defaultValue), modified: true };
  }

  return { numeric: numericMode, modified: false };
}

function validateOpacityMode(
  opacityMode: OpacityMode,
  stats: LayerStats,
  propertyPath: string,
  defaultValue: number,
  modifications: StyleModification[],
  columnMetadata?: ColumnMetadata[]
): { opacity: OpacityMode; modified: boolean } {
  if (opacityMode.mode === STYLE_MODE.FIXED) {
    return { opacity: opacityMode, modified: false };
  }

  const columnId = opacityMode.columnId;

  if (!columnExists(columnId, stats, columnMetadata)) {
    modifications.push({
      property: propertyPath,
      reason: 'column_not_found',
      details: `Column ${columnId} not found in stats`,
    });
    return { opacity: createDefaultOpacityMode(defaultValue), modified: true };
  }

  if (!isValidForRange(columnId, stats, columnMetadata)) {
    modifications.push({
      property: propertyPath,
      reason: 'column_type_mismatch',
      details: `Column ${columnId} is not valid for range (needs numeric type with 2+ distinct values)`,
    });
    return { opacity: createDefaultOpacityMode(defaultValue), modified: true };
  }

  return { opacity: opacityMode, modified: false };
}

function validateIconMode(
  iconMode: IconMode,
  stats: LayerStats,
  propertyPath: string,
  modifications: StyleModification[],
  columnMetadata?: ColumnMetadata[]
): { icon: IconMode; modified: boolean } {
  if (iconMode.mode === STYLE_MODE.FIXED) {
    return { icon: iconMode, modified: false };
  }
  const columnId = (iconMode as IconCategory).columnId;

  if (!columnExists(columnId, stats, columnMetadata)) {
    modifications.push({
      property: propertyPath,
      reason: 'column_not_found',
      details: `Column ${columnId} not found in stats`,
    });
    return { icon: createDefaultIconMode(), modified: true };
  }

  const categoryIcon = iconMode as IconCategory;
  if (categoryIcon.customIcons) {
    const validValues = getValidCategoryValues(columnId, stats, columnMetadata);
    const filteredIcons = categoryIcon.customIcons.filter((ci: IconCustomMapping) =>
      validValues.has(ci.category)
    );

    if (filteredIcons.length !== categoryIcon.customIcons.length) {
      modifications.push({
        property: propertyPath,
        reason: 'category_values_filtered',
        details: `Filtered ${categoryIcon.customIcons.length - filteredIcons.length} custom icons`,
      });
      return {
        icon: {
          ...categoryIcon,
          customIcons: filteredIcons,
        },
        modified: true,
      };
    }
  }

  return { icon: iconMode, modified: false };
}

function validateLabelStyle(
  label: LabelStyle | undefined,
  stats: LayerStats,
  propertyPath: string,
  modifications: StyleModification[],
  columnMetadata?: ColumnMetadata[]
): { label: LabelStyle | undefined; modified: boolean } {
  if (!label) {
    return { label, modified: false };
  }

  let modified = false;
  const validatedLabel = { ...label };

  if (label.columnId !== null && label.columnId !== undefined) {
    if (!columnExists(label.columnId, stats, columnMetadata)) {
      modifications.push({
        property: propertyPath,
        reason: 'column_not_found',
        details: `Label column ${label.columnId} not found in stats`,
      });
      validatedLabel.columnId = null;
      modified = true;
    }
  }

  if (label.labelText) {
    const { mode, parts } = label.labelText;
    const labelTextPropertyPath = `${propertyPath}.labelText`;

    if (mode === LABEL_MODE.SINGLE_COLUMN && parts?.length === 1 && parts[0].type === 'column') {
      const columnId = parts[0].columnId;
      if (!columnExists(columnId, stats, columnMetadata)) {
        modifications.push({
          property: labelTextPropertyPath,
          reason: 'column_not_found',
          details: `Label text column ${columnId} not found in stats`,
        });
        validatedLabel.labelText = {
          mode: LABEL_MODE.NONE,
        };
        modified = true;
      }
    } else if (mode === LABEL_MODE.TEMPLATE && parts) {
      const validatedParts = parts.filter(part => {
        if (part.type === 'column') {
          const exists = columnExists(part.columnId, stats, columnMetadata);
          if (!exists) {
            modifications.push({
              property: labelTextPropertyPath,
              reason: 'column_not_found',
              details: `Label text template column ${part.columnId} not found in stats`,
            });
            modified = true;
            return false;
          }
        }
        return true;
      });

      if (modified) {
        validatedLabel.labelText = {
          ...label.labelText,
          parts: validatedParts,
        };
      }
    }
  }

  return { label: validatedLabel, modified };
}

function validatePolygonStyle(
  polygon: PolygonStyle,
  stats: LayerStats,
  modifications: StyleModification[],
  columnMetadata?: ColumnMetadata[]
): { polygon: PolygonStyle; modified: boolean } {
  let modified = false;
  const validatedPolygon = { ...polygon };

  const fillColorResult = validateColorMode(
    polygon.fill.color,
    stats,
    'polygon.fill.color',
    modifications,
    { clampDataRange: true },
    columnMetadata
  );
  if (fillColorResult.modified) {
    validatedPolygon.fill = { ...polygon.fill, color: fillColorResult.color };
    modified = true;
  }

  const fillOpacityResult = validateOpacityMode(
    polygon.fill.opacity,
    stats,
    'polygon.fill.opacity',
    0.8,
    modifications,
    columnMetadata
  );
  if (fillOpacityResult.modified) {
    validatedPolygon.fill = { ...validatedPolygon.fill, opacity: fillOpacityResult.opacity };
    modified = true;
  }

  const borderColorResult = validateColorMode(
    polygon.border.color,
    stats,
    'polygon.border.color',
    modifications,
    {},
    columnMetadata
  );
  if (borderColorResult.modified) {
    validatedPolygon.border = { ...polygon.border, color: borderColorResult.color };
    modified = true;
  }

  const borderOpacityResult = validateOpacityMode(
    polygon.border.opacity,
    stats,
    'polygon.border.opacity',
    1,
    modifications,
    columnMetadata
  );
  if (borderOpacityResult.modified) {
    validatedPolygon.border = { ...validatedPolygon.border, opacity: borderOpacityResult.opacity };
    modified = true;
  }

  const borderWidthResult = validateNumericMode(
    polygon.border.width,
    stats,
    'polygon.border.width',
    1,
    modifications,
    columnMetadata
  );
  if (borderWidthResult.modified) {
    validatedPolygon.border = { ...validatedPolygon.border, width: borderWidthResult.numeric };
    modified = true;
  }

  if (polygon.label) {
    const labelResult = validateLabelStyle(
      polygon.label,
      stats,
      'polygon.label',
      modifications,
      columnMetadata
    );
    if (labelResult.modified) {
      validatedPolygon.label = labelResult.label;
      modified = true;
    }
  }

  return { polygon: validatedPolygon, modified };
}

function validatePointStyle(
  point: PointStyle,
  stats: LayerStats,
  modifications: StyleModification[],
  columnMetadata?: ColumnMetadata[]
): { point: PointStyle; modified: boolean } {
  let modified = false;
  const validatedPoint = { ...point };

  const iconResult = validateIconMode(
    point.icon,
    stats,
    'point.icon',
    modifications,
    columnMetadata
  );
  if (iconResult.modified) {
    validatedPoint.icon = iconResult.icon;
    modified = true;
  }

  const sizeResult = validateNumericMode(
    point.size,
    stats,
    'point.size',
    6,
    modifications,
    columnMetadata
  );
  if (sizeResult.modified) {
    validatedPoint.size = sizeResult.numeric;
    modified = true;
  }

  const colorResult = validateColorMode(
    point.color,
    stats,
    'point.color',
    modifications,
    { clampDataRange: true },
    columnMetadata
  );
  if (colorResult.modified) {
    validatedPoint.color = colorResult.color;
    modified = true;
  }

  const opacityResult = validateOpacityMode(
    point.opacity,
    stats,
    'point.opacity',
    1,
    modifications,
    columnMetadata
  );
  if (opacityResult.modified) {
    validatedPoint.opacity = opacityResult.opacity;
    modified = true;
  }

  const strokeColorResult = validateColorMode(
    point.strokeColor,
    stats,
    'point.strokeColor',
    modifications,
    {},
    columnMetadata
  );
  if (strokeColorResult.modified) {
    validatedPoint.strokeColor = strokeColorResult.color;
    modified = true;
  }

  const strokeOpacityResult = validateOpacityMode(
    point.strokeOpacity,
    stats,
    'point.strokeOpacity',
    1,
    modifications,
    columnMetadata
  );
  if (strokeOpacityResult.modified) {
    validatedPoint.strokeOpacity = strokeOpacityResult.opacity;
    modified = true;
  }

  const strokeWidthResult = validateNumericMode(
    point.strokeWidth,
    stats,
    'point.strokeWidth',
    1,
    modifications,
    columnMetadata
  );
  if (strokeWidthResult.modified) {
    validatedPoint.strokeWidth = strokeWidthResult.numeric;
    modified = true;
  }

  if (point.label) {
    const labelResult = validateLabelStyle(
      point.label,
      stats,
      'point.label',
      modifications,
      columnMetadata
    );
    if (labelResult.modified) {
      validatedPoint.label = labelResult.label;
      modified = true;
    }
  }

  return { point: validatedPoint, modified };
}

function validateLineStyle(
  line: LineStyle,
  stats: LayerStats,
  modifications: StyleModification[],
  columnMetadata?: ColumnMetadata[]
): { line: LineStyle; modified: boolean } {
  let modified = false;
  const validatedLine = { ...line };

  const colorResult = validateColorMode(
    line.color,
    stats,
    'line.color',
    modifications,
    { clampDataRange: true },
    columnMetadata
  );
  if (colorResult.modified) {
    validatedLine.color = colorResult.color;
    modified = true;
  }

  const opacityResult = validateOpacityMode(
    line.opacity,
    stats,
    'line.opacity',
    0.8,
    modifications,
    columnMetadata
  );
  if (opacityResult.modified) {
    validatedLine.opacity = opacityResult.opacity;
    modified = true;
  }

  const widthResult = validateNumericMode(
    line.width,
    stats,
    'line.width',
    3,
    modifications,
    columnMetadata
  );
  if (widthResult.modified) {
    validatedLine.width = widthResult.numeric;
    modified = true;
  }

  return { line: validatedLine, modified };
}

/**
 * Ensures all geometry sections exist in a StyleV2, adding defaults for missing ones.
 * This should always be called regardless of whether stats are available.
 *
 * @param style - The StyleV2 to ensure completeness
 * @returns Style with all geometry sections and modification metadata
 */
export function ensureAllGeometrySections(style: StyleV2): StyleValidationResult {
  const modifications: StyleModification[] = [];
  let wasModified = false;

  const defaults = getDefaultStyles();

  const validatedStyle: StyleV2 = {
    version: style.version,
    polygon: style.polygon ?? defaults.polygon!,
    point: style.point ?? defaults.point!,
    line: style.line ?? defaults.line!,
    // Carried through rather than defaulted: raster is not a geometry section,
    // and rebuilding without it drops a collaborator's opacity change on the way
    // in, so their edit never reaches the map.
    raster: style.raster,
  };

  if (!style.polygon) {
    modifications.push({
      property: 'polygon',
      reason: 'section_missing',
      details: 'Added default polygon style (geometry section was missing)',
    });
    wasModified = true;
  }

  if (!style.point) {
    modifications.push({
      property: 'point',
      reason: 'section_missing',
      details: 'Added default point style (geometry section was missing)',
    });
    wasModified = true;
  }

  if (!style.line) {
    modifications.push({
      property: 'line',
      reason: 'section_missing',
      details: 'Added default line style (geometry section was missing)',
    });
    wasModified = true;
  }

  return {
    validatedStyle,
    wasModified,
    modifications,
  };
}

export function validateStyleV2AgainstStats(
  style: StyleV2,
  stats: LayerStats,
  columnMetadata?: ColumnMetadata[]
): StyleValidationResult {
  const modifications: StyleModification[] = [];
  let wasModified = false;

  const ensuredResult = ensureAllGeometrySections(style);
  if (ensuredResult.wasModified) {
    wasModified = true;
    modifications.push(...ensuredResult.modifications);
  }

  const validatedStyle: StyleV2 = {
    version: ensuredResult.validatedStyle.version,
  };

  const polygonResult = validatePolygonStyle(
    ensuredResult.validatedStyle.polygon!,
    stats,
    modifications,
    columnMetadata
  );
  validatedStyle.polygon = polygonResult.polygon;
  if (polygonResult.modified) wasModified = true;

  const pointResult = validatePointStyle(
    ensuredResult.validatedStyle.point!,
    stats,
    modifications,
    columnMetadata
  );
  validatedStyle.point = pointResult.point;
  if (pointResult.modified) wasModified = true;

  const lineResult = validateLineStyle(
    ensuredResult.validatedStyle.line!,
    stats,
    modifications,
    columnMetadata
  );
  validatedStyle.line = lineResult.line;
  if (lineResult.modified) wasModified = true;

  return {
    validatedStyle,
    wasModified,
    modifications,
  };
}
